/**
 * The browser backend of the voice interface (types.ts): WebRTC media to
 * comms-voice, WebSocket signalling at `/voice/ws`.
 *
 * As in spike PR #4: one send-only mic transceiver and N receive-only
 * slots, offered once, so joins and moves never renegotiate. Muting uses
 * `replaceTrack(null)`, so no packets leave (not `track.enabled = false`,
 * which keeps sending silence); so does being blocked (a server or channel
 * mute, no Speak here: nobody would hear it). Levels come from one
 * `getStats()` per tick. An idle connection sends a signalling `ping`
 * every 15 s (bandwidth, 2026-10-29); comms-voice's own WebSocket pings
 * keep it alive, so its idle timeout (30 s) does not depend on ours.
 *
 * Also used by the web build inside the desktop app (WebView2 is Chromium).
 */
import {
  VOICE_RECEIVE_SLOTS,
  type NoiseSuppression,
  type Pid,
  type Snowflake,
} from '@neosocial/protocol';
import {
  VoiceDeniedError,
  clampVolume,
  elementVolumeWorks,
  slotPlayback,
  type ClientMessage,
  type ServerMessage,
  type JoinError,
  type JoinOptions,
  type JoinResult,
  type MediaState,
  type MicResult,
  type Voice,
  type VoiceEvent,
  type VoiceEventOf,
  type VoiceEventType,
  type VoiceMedia,
} from './types';
import { openMicStream } from './mic';
import { micConstraints, processMic, type ProcessedMic } from './noise';
import { createMediaEngine, type MediaEngine } from './webMedia';

export interface WebVoiceOptions {
  slots?: number;
  /** The microphone level's tick (voice activity), from the local analyser. */
  levelMs?: number;
  /** Between `getStats` calls (round trip, packet counts, peers' levels). */
  statsMs?: number;
  /** How long a move waits for the engine. */
  replyMs?: number;
  /** How long `join` waits for `welcome`. */
  welcomeMs?: number;
  /** Between signalling pings (the round trip when ICE has none); `VOICE_PING_MS`. */
  pingMs?: number;
}

/**
 * The signalling ping's period: 15 s, not 5 (bandwidth, 2026-10-29). Well
 * inside comms-voice's `ws_idle_secs` (30), which its own pings keep fed.
 */
export const VOICE_PING_MS = 15_000;

/**
 * `getStats` once a second, not on every level tick (voice performance,
 * 2026-11-01): voice activity reads the local analyser, rings follow
 * comms-voice's `speaking`, so the report (a few hundred objects with 33
 * audio streams) is only for the round trip and the counters.
 */
export const VOICE_STATS_MS = 1000;

interface Slot {
  tr: RTCRtpTransceiver;
  audio: HTMLAudioElement;
  /** Web Audio boost above 100 % (created on first use). */
  boost: { src: MediaStreamAudioSourceNode; gain: GainNode } | null;
  pid: Pid | null;
  packets: number;
  lost: number;
  level: number;
  active: boolean;
}

interface Pending {
  resolve: (ev: ServerMessage) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  t0: number;
}

type AnyFn = (ev: VoiceEvent) => void;

export function createWebVoice(options: WebVoiceOptions = {}): Voice {
  const o = {
    slots: Math.min(options.slots ?? VOICE_RECEIVE_SLOTS, VOICE_RECEIVE_SLOTS),
    levelMs: options.levelMs ?? 100,
    statsMs: options.statsMs ?? VOICE_STATS_MS,
    replyMs: options.replyMs ?? 5000,
    welcomeMs: options.welcomeMs ?? 10_000,
    pingMs: options.pingMs ?? VOICE_PING_MS,
  };
  const fns = new Map<string, AnyFn[]>();
  let pc: RTCPeerConnection | null = null;
  let ws: WebSocket | null = null;
  let mic: MediaStreamTrack | null = null;
  /** What `mic` comes from: the raw track and its noise suppression (voice/noise.ts). */
  let micProc: ProcessedMic | null = null;
  /** Noise suppression asked for, and the microphone it applies to (2026-11-01). */
  let noise: NoiseSuppression = 'standard';
  let inputDevice: string | null = null;
  /** The priority-speaker key is held (2026-11-01). */
  let priority = false;
  /** Input since the last ping: the next one says `active` (the AFK timer). */
  let inputSince = false;
  let sender: RTCRtpSender | null = null;
  let slots: Slot[] = [];
  const pending = new Map<number, Pending>();
  let req = 0;
  let levelTimer: ReturnType<typeof setInterval> | undefined;
  let pingTimer: ReturnType<typeof setInterval> | undefined;
  let selfMute = false;
  let selfDeaf = false;
  let gate = false;
  /** Nobody may hear us now (`block`): nothing is sent. */
  let blocked = false;
  let leaving = false;
  /** Ends a join still waiting for `welcome` (a leave mid-join, HV-B02). */
  let abortJoin: ((e: Error) => void) | null = null;
  /** The channel's bitrate: the most the microphone sends (HV-B04). */
  let bitrate: number | null = null;
  let state: MediaState = 'closed';
  let pid: Pid | null = null;
  let channel: Snowflake | null = null;
  let rtt: number | null = null;
  let pingRtt: number | null = null;
  let outPackets = 0;
  let outputDevice: string | null = null;
  /** The master output volume (`setOutputVolume`): every slot plays at its own volume times this. */
  let outputVolume = 1;
  const volumes = new Map<Pid, number>();
  const localMutes = new Set<Pid>();
  // a local analyser on the mic: the input level even while the gate is shut
  let actx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  const buf = new Float32Array(1024);
  /** Screen share and camera (webMedia.ts), once the hello's offer is set. */
  let media: MediaEngine | null = null;

  function emit(ev: VoiceEvent): void {
    for (const fn of (fns.get(ev.t) ?? []).slice()) safe(fn, ev);
    for (const fn of (fns.get('*') ?? []).slice()) safe(fn, ev);
  }
  function safe(fn: AnyFn, ev: VoiceEvent) {
    try {
      fn(ev);
    } catch (e) {
      console.error(e);
    }
  }
  function setState(s: MediaState) {
    if (state === s) return;
    state = s;
    emit({ t: 'state', state: s });
  }

  /** Packets leave only while this holds; otherwise the sender has no track. */
  function sending(): boolean {
    return !!mic && gate && !selfMute && !selfDeaf && !blocked;
  }
  /**
   * The mute others see: yours, or no microphone at all (listen-only is
   * shown as "mic off", never as a working mic, HV-B08).
   */
  function shownMute(): boolean {
    return selfMute || !mic;
  }
  function tellMute(): void {
    sendMsg({ t: 'mute', self_mute: shownMute(), self_deaf: selfDeaf });
  }
  /** Caps the Opus encoder at the channel's bitrate (HV-B04). */
  function applyBitrate(): void {
    const s = sender;
    if (!s) return;
    try {
      const p = s.getParameters();
      const enc: RTCRtpEncodingParameters[] = p.encodings?.length ? p.encodings : [{}];
      if (bitrate) enc[0]!.maxBitrate = bitrate;
      else delete enc[0]!.maxBitrate;
      p.encodings = enc;
      void s.setParameters(p).catch(() => {});
    } catch {
      /* no parameters yet: applied after the answer */
    }
  }
  // what we last told comms-voice about transmitting (others' rings follow it)
  let toldSpeaking = false;
  let toldPriority = false;
  function applySend(): void {
    const on = sending();
    if (sender) void sender.replaceTrack(on ? mic : null).catch(() => {});
    const prio = on && priority;
    if ((on !== toldSpeaking || prio !== toldPriority) && ws && ws.readyState === WebSocket.OPEN) {
      toldSpeaking = on;
      toldPriority = prio;
      sendMsg(prio ? { t: 'speaking', on, priority: true } : { t: 'speaking', on });
    }
  }
  function ping(): void {
    const active = inputSince;
    inputSince = false;
    sendMsg(
      active ? { t: 'ping', ts: performance.now(), active } : { t: 'ping', ts: performance.now() },
    );
  }
  function stopMic(): void {
    if (micProc) micProc.stop();
    else mic?.stop();
    micProc = null;
    mic = null;
  }

  function sendMsg(m: ClientMessage): void {
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
  }

  function request<T extends ServerMessage>(
    m: ClientMessage & { req: number },
    ms: number = o.replyMs,
  ): Promise<T> {
    if (!ws || ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(m.req);
        reject(new Error('no answer from the voice server'));
      }, ms);
      pending.set(m.req, {
        resolve: resolve as (ev: ServerMessage) => void,
        reject,
        timer,
        t0: performance.now(),
      });
      sendMsg(m);
    });
  }

  function slotFor(p: Pid): Slot | undefined {
    return slots.find((s) => s.pid === p);
  }
  // one playback context for boosted people (volume above 100 %)
  let playCtx: AudioContext | null = null;
  function boostFor(s: Slot): Slot['boost'] {
    if (s.boost) return s.boost;
    try {
      playCtx ??= new AudioContext();
      if (outputDevice)
        void (playCtx as AudioContext & { setSinkId?: (id: string) => Promise<void> })
          .setSinkId?.(outputDevice)
          .catch(() => {});
      const src = playCtx.createMediaStreamSource(new MediaStream([s.tr.receiver.track]));
      const gain = playCtx.createGain();
      src.connect(gain).connect(playCtx.destination);
      s.boost = { src, gain };
      // a deafen keeps it suspended (applyDeafPlayback)
      if (!selfDeaf) void playCtx.resume().catch(() => {});
    } catch {
      s.boost = null;
    }
    return s.boost;
  }
  /**
   * While deafened nothing plays: the slots' elements are paused and the
   * boost context suspended, so no audio is pulled, mixed or rendered
   * (comms-voice sends none either, 2026-11-01); undeafening plays them
   * again at once (a live stream has nothing to catch up).
   */
  function applyPlayback(s: Slot): void {
    try {
      if (selfDeaf) s.audio.pause();
      else void s.audio.play().catch(() => {});
    } catch {
      /* no media element (tests) */
    }
  }
  function applyDeafPlayback(): void {
    for (const s of slots) applyPlayback(s);
    try {
      if (playCtx) void (selfDeaf ? playCtx.suspend() : playCtx.resume()).catch(() => {});
    } catch {
      /* closed */
    }
  }

  function applySlotAudio(s: Slot): void {
    const muted = selfDeaf || (s.pid !== null && localMutes.has(s.pid));
    const plan = slotPlayback(
      (s.pid !== null ? (volumes.get(s.pid) ?? 1) : 1) * outputVolume,
      muted,
      elementVolumeWorks(),
    );
    const boost = plan.gain !== null ? boostFor(s) : s.boost;
    if (plan.gain !== null && !boost) {
      // no Web Audio: play at 100 % rather than not at all
      s.audio.muted = muted;
      s.audio.volume = 1;
      return;
    }
    s.audio.muted = plan.elementMuted;
    s.audio.volume = plan.elementVolume;
    if (boost) boost.gain.gain.value = plan.gain ?? 0;
  }

  function onServer(ev: ServerMessage): void {
    // the video half first, so a slot's stream is ready when the screens look
    media?.onServer(ev);
    switch (ev.t) {
      case 'welcome':
        pid = ev.pid;
        channel = ev.channel;
        break;
      case 'moved':
        channel = ev.channel;
        break;
      case 'slot': {
        const s = slots[ev.slot];
        if (s) {
          s.pid = ev.pid;
          applySlotAudio(s);
        }
        break;
      }
      case 'left':
        for (const s of slots) if (s.pid === ev.pid) s.pid = null;
        break;
      case 'pong':
        pingRtt = Math.max(0, performance.now() - ev.ts);
        break;
    }
    const r = 'req' in ev ? ev.req : 0;
    const p = r ? pending.get(r) : undefined;
    if (p) {
      pending.delete(r);
      clearTimeout(p.timer);
      if (ev.t === 'denied') p.reject(new VoiceDeniedError(ev.reason, ev));
      else p.resolve(ev);
    }
    emit(ev);
  }

  function inputLevel(): number {
    if (!analyser) return 0;
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += (buf[i] ?? 0) * (buf[i] ?? 0);
    // RMS on a 0..1 scale comparable with WebRTC's audioLevel
    return Math.min(1, Math.sqrt(sum / buf.length) * 2);
  }

  /** The microphone's level after the last `getStats` (media-source). */
  let outLevel = 0;
  /** When the last `getStats` was asked for (performance.now), or one is running. */
  let statsAt = -Infinity;
  let statsBusy = false;

  /** The round trip, the counters and each slot's level, from one `getStats`. */
  function readStats(conn: RTCPeerConnection): void {
    statsBusy = true;
    statsAt = performance.now();
    const ids = slots.map((s) => s.tr.receiver.track?.id);
    void conn
      .getStats()
      .then((rep) => {
        if (pc !== conn) return;
        let candRtt: number | null = null;
        rep.forEach((r: Record<string, unknown> & { type: string }) => {
          if (r.type === 'outbound-rtp' && r.kind === 'audio')
            outPackets = Number(r.packetsSent ?? 0);
          else if (r.type === 'media-source' && r.kind === 'audio')
            outLevel = Number(r.audioLevel ?? 0);
          else if (
            r.type === 'candidate-pair' &&
            r.nominated &&
            r.state === 'succeeded' &&
            r.currentRoundTripTime != null
          )
            candRtt = Number(r.currentRoundTripTime) * 1000;
          else if (r.type === 'inbound-rtp' && r.kind === 'audio') {
            const i = ids.indexOf(r.trackIdentifier as string);
            const s = i >= 0 ? slots[i] : undefined;
            if (!s) return;
            const got = Number(r.packetsReceived ?? 0);
            s.active = got > s.packets;
            s.packets = got;
            s.lost = Number(r.packetsLost ?? 0);
            s.level = Number(r.audioLevel ?? 0);
          }
        });
        rtt = candRtt && candRtt > 0 ? candRtt : pingRtt;
      })
      .catch(() => {})
      .finally(() => {
        statsBusy = false;
      });
  }

  /**
   * One level tick: the microphone's level for voice activity (the local
   * analyser), with what the last `getStats` said; a new `getStats` every
   * `statsMs`.
   */
  function poll(): void {
    const conn = pc;
    if (!conn) return;
    if (!statsBusy && performance.now() - statsAt >= o.statsMs) readStats(conn);
    const input = inputLevel();
    emit({
      t: 'levels',
      me: sending() ? Math.max(outLevel, input) : 0,
      input,
      sent: outPackets,
      rtt_ms: rtt === null ? null : Math.round(rtt * 10) / 10,
      peers: slots
        .map((s, i) => ({
          slot: i,
          pid: s.pid as Pid,
          level: s.level,
          packets: s.packets,
          lost: s.lost,
          active: s.active,
        }))
        .filter((p) => p.pid !== null),
    });
  }

  /**
   * Opens the chosen microphone; when it is gone (unplugged, so
   * `getUserMedia` refuses its exact id), the system default one, as
   * Discord does (HV-B07, ST-B05). `fallback` says the default stands in
   * for it. The same opening as "Test microphone" (voice/mic.ts): a refused
   * permission is not "missing" and tries no other device.
   */
  async function openMic(
    deviceId?: string | null,
  ): Promise<{ proc: ProcessedMic | null; fallback: boolean }> {
    try {
      const m = await openMicStream(deviceId, { channelCount: 1, ...micConstraints(noise) });
      const track = m.stream.getAudioTracks()[0] ?? null;
      if (!track) return { proc: null, fallback: false };
      // Strong noise suppression runs RNNoise on it (voice/noise.ts)
      return { proc: await processMic(track, noise), fallback: m.fellBack };
    } catch {
      return { proc: null, fallback: false };
    }
  }

  function attachAnalyser(track: MediaStreamTrack | null): void {
    try {
      void actx?.close();
    } catch {
      /* ignore */
    }
    actx = null;
    analyser = null;
    if (!track) return;
    try {
      actx = new AudioContext();
      const src = actx.createMediaStreamSource(new MediaStream([track]));
      analyser = actx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
    } catch {
      analyser = null;
    }
  }

  function gathered(conn: RTCPeerConnection): Promise<void> {
    return new Promise((done) => {
      if (conn.iceGatheringState === 'complete') return done();
      const t = setTimeout(done, 2000);
      conn.addEventListener('icegatheringstatechange', () => {
        if (conn.iceGatheringState === 'complete') {
          clearTimeout(t);
          done();
        }
      });
    });
  }

  /**
   * Back in view: iOS may have stopped the level meter's and the boosted
   * voices' audio contexts while the page was hidden, and voice activity
   * reads silence until they run again (owner, 2026-11-03).
   */
  function resumeOnShow(): void {
    if (document.visibilityState !== 'visible') return;
    for (const c of [actx, selfDeaf ? null : playCtx])
      if (c && c.state !== 'running' && c.state !== 'closed') void c.resume().catch(() => {});
  }

  function teardown(): void {
    media?.teardown();
    media = null;
    clearInterval(levelTimer);
    clearInterval(pingTimer);
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', resumeOnShow);
    for (const [, p] of pending) {
      clearTimeout(p.timer);
      p.reject(new Error('left'));
    }
    pending.clear();
    for (const s of slots) {
      s.audio.pause();
      s.audio.srcObject = null;
      s.boost?.src.disconnect();
    }
    void playCtx?.close().catch(() => {});
    playCtx = null;
    slots = [];
    if (ws) {
      ws.onclose = ws.onmessage = ws.onerror = null;
      try {
        ws.close(1000, 'leave');
      } catch {
        /* ignore */
      }
    }
    pc?.close();
    stopMic();
    attachAnalyser(null);
    pc = null;
    ws = null;
    sender = null;
    pid = null;
    channel = null;
    rtt = pingRtt = null;
    toldSpeaking = false;
    statsAt = -Infinity;
    statsBusy = false;
    outLevel = 0;
    toldPriority = false;
    inputSince = false;
  }

  async function join(jo: JoinOptions): Promise<JoinResult> {
    if (pc) throw new Error('already joined');
    leaving = false;
    selfMute = jo.selfMute;
    selfDeaf = jo.selfDeaf;
    // a new connection's roster says again whether we may be heard
    blocked = false;
    outputDevice = jo.outputDeviceId ?? null;
    outputVolume = clampVolume(jo.outputVolume ?? 1);
    bitrate = jo.bitrate ?? null;
    noise = jo.noiseSuppression ?? 'standard';
    inputDevice = jo.inputDeviceId ?? null;
    setState('connecting');
    const t0 = performance.now();
    // A leave while a step is awaited ends the join there: nothing more opens
    // and no late `hello` replaces the connection that came after (HV-B02).
    const stillJoining = () => {
      if (leaving) throw new Error('left');
    };
    try {
      const opened = await openMic(jo.inputDeviceId);
      micProc = opened.proc;
      mic = opened.proc?.track ?? null;
      stillJoining();
      attachAnalyser(mic);
      const conn = new RTCPeerConnection({ iceServers: jo.iceServers ?? [] });
      pc = conn;
      if (typeof document !== 'undefined')
        document.addEventListener('visibilitychange', resumeOnShow);
      sender = mic
        ? conn.addTransceiver(mic, { direction: 'sendonly' }).sender
        : conn.addTransceiver('audio', { direction: 'sendonly' }).sender;
      slots = [];
      for (let i = 0; i < o.slots; i++) {
        const tr = conn.addTransceiver('audio', { direction: 'recvonly' });
        const audio = new Audio();
        audio.autoplay = true;
        audio.srcObject = new MediaStream([tr.receiver.track]);
        if (outputDevice)
          void (audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> })
            .setSinkId?.(outputDevice)
            .catch(() => {});
        const s: Slot = {
          tr,
          audio,
          boost: null,
          pid: null,
          packets: 0,
          lost: 0,
          level: 0,
          active: false,
        };
        applySlotAudio(s);
        // joined deafened: nothing plays until the undeafen
        if (selfDeaf) applyPlayback(s);
        slots.push(s);
      }
      conn.onconnectionstatechange = () => {
        if (pc !== conn) return;
        const cs = conn.connectionState;
        if (cs === 'connected') setState('connected');
        else if (cs === 'failed') setState('failed');
        else if (cs === 'closed') setState('closed');
      };
      // offer with the mic attached (the SSRC is in the SDP), then shut the gate until we transmit
      await conn.setLocalDescription(await conn.createOffer());
      stillJoining();
      if (!sending()) await sender.replaceTrack(null);
      // the hello's m-lines (mic and audio slots) stay as they are; video comes later
      media = createMediaEngine(
        {
          pc: () => pc,
          request: (m, ms) => request(m, ms),
          send: sendMsg,
          nextReq: () => ++req,
          deaf: () => selfDeaf,
          outputDevice: () => outputDevice,
          outputVolume: () => outputVolume,
        },
        new Set((conn.getTransceivers?.() ?? []).map((t) => t.mid).filter((m): m is string => !!m)),
      );
      await gathered(conn);
      stillJoining();
      const offer = conn.localDescription?.sdp ?? '';

      const welcome = await new Promise<VoiceEventOf<'welcome'>>((resolve, reject) => {
        abortJoin = reject;
        const sock = new WebSocket(jo.url);
        ws = sock;
        const to = setTimeout(
          () => reject(new Error('the voice server did not answer')),
          o.welcomeMs,
        );
        let welcomed = false;
        // comms-voice's reason for refusing the join ("full"...), sent before it closes
        let denied: string | null = null;
        sock.onopen = () => {
          sendMsg({
            t: 'hello',
            token: jo.token,
            offer,
            slots: o.slots,
            self_mute: shownMute(),
            self_deaf: selfDeaf,
            ...(jo.password ? { password: jo.password } : {}),
          });
        };
        sock.onmessage = (e) => {
          let m: ServerMessage;
          try {
            m = JSON.parse(String(e.data)) as ServerMessage;
          } catch {
            return;
          }
          if (m.t === 'welcome' && !welcomed) {
            welcomed = true;
            clearTimeout(to);
            onServer(m);
            resolve(m);
            return;
          }
          if (m.t === 'denied' && !welcomed) denied = m.reason;
          onServer(m);
        };
        sock.onerror = () => {
          /* onclose follows */
        };
        sock.onclose = (e) => {
          if (ws !== sock) return;
          if (!welcomed) {
            clearTimeout(to);
            const err: JoinError = new Error(e.reason || `voice signalling closed (${e.code})`);
            err.code = e.code;
            if (denied) err.reason = denied;
            reject(err);
            return;
          }
          emit({ t: 'signal_closed', code: e.code, reason: e.reason });
          if (!leaving) setState('closed');
        };
      });
      abortJoin = null;
      stillJoining();
      await conn.setRemoteDescription({ type: 'answer', sdp: welcome.answer });
      stillJoining();
      applyBitrate();
      levelTimer = setInterval(poll, o.levelMs);
      pingTimer = setInterval(ping, o.pingMs);
      ping();
      return {
        pid: welcome.pid,
        channel: welcome.channel,
        members: welcome.members,
        join_ms: Math.round(performance.now() - t0),
        listenOnly: !mic,
        micFallback: opened.fallback,
      };
    } catch (e) {
      abortJoin = null;
      teardown();
      setState(leaving ? 'closed' : 'failed');
      throw e;
    }
  }

  /** The video calls, handed to the engine while connected. */
  const mediaApi: VoiceMedia = {
    publish(source, tracks, quality) {
      return media
        ? media.publish(source, tracks, quality)
        : Promise.reject(new VoiceDeniedError('unsupported'));
    },
    unpublish: (source) => media?.unpublish(source),
    watch: (p, on) => (media ? media.watch(p, on) : Promise.reject(new Error('not connected'))),
    setVisible: (sources) => media?.setVisible(sources),
    stream: (p, source) => media?.stream(p, source) ?? null,
    setStreamVolume: (p, v) => media?.setStreamVolume(p, v),
    setQuality: (source, q) => media?.setQuality(source, q),
    get publishing() {
      return media?.publishing ?? [];
    },
  };

  return {
    kind: 'web',
    join,
    media: mediaApi,
    move(ch, password) {
      const m: ClientMessage & { req: number } = { t: 'move', req: ++req, channel: ch };
      if (password != null) m.password = password;
      return request(m);
    },
    mute(on) {
      selfMute = on;
      applySend();
      tellMute();
    },
    transmit(on) {
      gate = on;
      applySend();
    },
    block(on) {
      blocked = on;
      applySend();
    },
    deafen(on) {
      selfDeaf = on;
      for (const s of slots) applySlotAudio(s);
      applyDeafPlayback();
      media?.onDeafen();
      applySend();
      tellMute();
    },
    localMute(p, muted) {
      if (muted) localMutes.add(p);
      else localMutes.delete(p);
      const s = slotFor(p);
      if (s) applySlotAudio(s);
      sendMsg({ t: 'local_mute', pid: p, muted });
    },
    setVolume(p, v) {
      volumes.set(p, clampVolume(v));
      const s = slotFor(p);
      if (s) applySlotAudio(s);
    },
    setOutputVolume(v) {
      outputVolume = clampVolume(v);
      for (const s of slots) applySlotAudio(s);
      media?.onOutputVolume();
    },
    setBitrate(bps) {
      bitrate = bps;
      applyBitrate();
    },
    async setInputDevice(id): Promise<MicResult | null> {
      if (!pc) return null;
      const conn = pc;
      inputDevice = id;
      const { proc: next, fallback } = await openMic(id);
      if (pc !== conn) {
        next?.stop();
        return null;
      }
      if (!next) return { ok: !!mic, fallback: false };
      const had = !!mic;
      stopMic();
      micProc = next;
      mic = next.track;
      attachAnalyser(mic);
      applySend();
      // a microphone where there was none: others stop seeing "no mic" (HV-B08)
      if (!had) tellMute();
      return { ok: true, fallback };
    },
    async setNoiseSuppression(mode) {
      if (mode === noise && pc) return micProc?.mode ?? noise;
      noise = mode;
      if (!pc || !mic) return null;
      // the microphone opens again with the new processing (Discord swaps it in place)
      await this.setInputDevice(inputDevice);
      return micProc?.mode ?? noise;
    },
    get noiseSuppression() {
      return micProc?.mode ?? noise;
    },
    setPriority(on) {
      priority = on;
      applySend();
    },
    noteInput() {
      inputSince = true;
    },
    async setOutputDevice(id) {
      outputDevice = id;
      media?.setOutputDevice(id);
      void (playCtx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null)
        ?.setSinkId?.(id ?? '')
        .catch(() => {});
      await Promise.all(
        slots.map((s) =>
          (s.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> })
            .setSinkId?.(id ?? '')
            .catch(() => {}),
        ),
      );
    },
    leave() {
      leaving = true;
      // a join still waiting for `welcome` ends now, not at its timeout
      abortJoin?.(new Error('left'));
      abortJoin = null;
      if (!pc && !ws) return;
      sendMsg({ t: 'leave' });
      teardown();
      setState('closed');
    },
    on(t, fn) {
      const list = fns.get(t) ?? [];
      list.push(fn as AnyFn);
      fns.set(t, list);
      return () =>
        fns.set(
          t,
          (fns.get(t) ?? []).filter((f) => f !== fn),
        );
    },
    get state() {
      return state;
    },
    get pid() {
      return pid;
    },
    get channel() {
      return channel;
    },
  } satisfies Voice;
}

export type { VoiceEventType };
